import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { useReport } from '@/app/dashboards/composables/useReport';
import { computed } from 'vue';
import {
  CHART_DATA_LIMIT,
  DATA_TYPES,
  LISTENING_DATA_TYPES,
  sentimentColorMap,
  TOTAL_METRIC_GROUPS,
  ONLINE_FOLLOWER_HOUR_BUCKET_SIZE,
} from '@/app/dashboards/constants';
import {
  ALL_CHANNELS_KEY,
  getChannelConfigProp,
  getChannelText,
} from '@/models/dashboards/channels.enum';
import { floor, map, pick } from 'lodash-es';
import { GRAPH_REPORT_TYPES, METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import { canAccessCompetitor } from '@/app/dashboards/utils/competitive.utils';
import { DEFAULT_TIME_FORMAT, TIME_FORMATS } from '@/components/foundation/charts/chartjs.defaults';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import {
  AGGREGATE_REPORT_TYPES,
  BASE_BREAKDOWN_OPTIONS,
  LISTENING_REPORT_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';
import { useAuthStore } from '@/stores/auth';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import { sortChartData } from '@/components/foundation/charts/chartjs.utils';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import { formatGoal } from '@/app/dashboards/utils/report.utils';
import { useMetricFormatting } from '@/app/dashboards/composables/useMetricFormattingSetup';
import { useHorizontalBarReportDetails } from '@/app/dashboards/composables/useHorizontalBarReportDetails';
import { Formatters } from '@dashhudson/dashing-ui';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import { useReportMetaAggregateBy } from '@/app/dashboards/composables/useReportMetaAggregateBy';
import { formatDatePattern } from '@/utils/formatters';
import dayjs from 'dayjs';

export function useGraphReportDetails({ report, graphScale } = {}) {
  const authStore = useAuthStore();
  const dashboardReportsStore = useDashboardReportsStore();

  const {
    aggregateBy,
    availableAssignees,
    secondaryAggregateBy,
    firstChannel,
    firstMetric,
    isCustomMetricReportTile,
    isMultiChannel,
    isMultiMetric,
    metrics,
    reportCategory,
    reportChannels,
    reportMeta,
    showGoalChip,
    reportData,
    formattedReportDataForTableAndGraphReports,
    reportMetricDetails,
    reportType,
    currency,
    displayStrategy,
  } = useReport({
    report,
  });

  const { disableAggregationSort } = useReportMetaAggregateBy({ aggregateBy });

  const { dataType } = useMetricDetails({
    metric: firstMetric,
    channels: reportChannels,
    metricReportTypeKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  });

  const { formatCustomMetricValue, getCustomReportDetailsFromId } = useCustomMetrics();
  const { isCommunityReportCategory, isTSIReportCategory, isListeningReportCategory } =
    useReportCategory({ reportCategory });
  const {
    isCreatorReport,
    isLineChart,
    isStackedBarChart,
    isBarChart,
    isAdsStackedBarChart,
    isDoughnutChart,
    isHorizontalBarChart,
  } = useReportType({
    reportType,
  });

  const isTimeSeriesStackedBarChart = computed(
    () => isStackedBarChart.value && firstMetric.value === 'ONLINE_FOLLOWERS',
  );

  const customMetricDetails = computed(() => getCustomReportDetailsFromId(firstMetric.value));

  const { getFormatConfig, isDurationMetric } = useMetricFormatting({
    dataType,
    isCustomMetricReportTile,
    customMetricDetails,
    currency,
    displayStrategy,
  });
  // TODO [SC:177226] move configs to formatMetricValue so that the composable isn't called in as many places
  function getFormattedValue(value, isHover = false) {
    if (isCustomMetricReportTile.value)
      return formatCustomMetricValue(value, customMetricDetails.value.metricFormat);

    const config = getFormatConfig({
      value,
      disableAbbreviation: true,
      isHover,
      isDoughnutChart: isDoughnutChart.value,
    });
    const formatTypeMap = {
      percent: Formatters.FORMAT_TYPES.PERCENT,
      currency: Formatters.FORMAT_TYPES.CURRENCY,
      number: Formatters.FORMAT_TYPES.NUMBER,
      duration: Formatters.FORMAT_TYPES.DURATION,
      abbr: Formatters.FORMAT_TYPES.ABBREVIATED,
      normal: Formatters.FORMAT_TYPES.NUMBER,
    };
    const formatterType = formatTypeMap[config.format] || Formatters.FORMAT_TYPES.NUMBER;

    return Formatters.formatNumber(value, formatterType, {
      formatOptions: config.formatOptions,
      durationOptions: config.durationOptions,
    });
  }

  const chartFormatConfig = computed(() =>
    getFormatConfig({
      value: undefined,
      disableAbbreviation: true,
      isDoughnutChart: isDoughnutChart.value,
    }),
  );

  const useChannelDataset = computed(
    () =>
      aggregateBy.value === TOTAL_METRIC_GROUPS.CHANNEL ||
      (isLineChart.value &&
        isMultiChannel.value &&
        !isCustomMetricReportTile.value &&
        !isListeningReportCategory.value),
  );

  const useMetricDataset = computed(() => isLineChart.value && isMultiMetric.value);

  const useTags = computed(
    () => !isLineChart.value && aggregateBy.value === TOTAL_METRIC_GROUPS.TAG,
  );

  const useSentiment = computed(() => aggregateBy.value === 'SENTIMENT');

  const useMessageType = computed(() => aggregateBy.value === 'MESSAGE_TYPE');

  const useAssignee = computed(() => aggregateBy.value === 'ASSIGNEE');

  const useAgeGenderDataset = computed(() => aggregateBy.value === TOTAL_METRIC_GROUPS.AGE_GENDER);

  const graphType = computed(() => {
    if (isLineChart.value) return GRAPH_REPORT_TYPES.LINE;
    if (isHorizontalBarChart.value) return GRAPH_REPORT_TYPES.HORIZONTAL_BAR;
    if (isStackedBarChart.value) return GRAPH_REPORT_TYPES.STACKED_BAR;
    if (isBarChart.value) return GRAPH_REPORT_TYPES.BAR;
    if (isDoughnutChart.value) return GRAPH_REPORT_TYPES.DOUGHNUT;
    return {};
  });

  const timeUnit = computed(() => GRAPH_SCALES?.[graphScale?.value]?.chartJSTimeUnit);

  const showCompetitorAverage = computed(() => {
    const data = reportData.value ?? [];
    const competitorCount = Object.values(data).reduce((acc, accountData) => {
      if (accountData?.data_type === DATA_TYPES.COMPETITOR) return acc + 1;
      return acc;
    }, 0);
    return competitorCount > 1;
  });

  const goalLineOptions = computed(() => {
    if (!showGoalChip.value) return {};
    return {
      plugins: {
        markerLine: {
          markers: [
            {
              ...formatGoal(
                reportMeta.value.goals?.overall?.type,
                reportMeta.value.goals?.overall?.value,
              ),
              legend: {
                label: 'Set Goal',
              },
            },
          ],
        },
      },
    };
  });

  const goalValues = computed(() => {
    if (!showGoalChip.value) return [];
    const formattedGoal = formatGoal(
      reportMeta.value.goals?.overall?.type,
      reportMeta.value.goals?.overall?.value,
    );
    return formattedGoal?.value != null ? [formattedGoal.value] : [];
  });

  const competitorTagsById = computed(() => {
    if (!useTags.value) return {};

    return dashboardReportsStore.competitorTags.reduce((acc, brandData) => {
      const brandCompetitorTagsById = (brandData?.tags ?? []).reduce((innerAcc, tagData) => {
        innerAcc[tagData.id] = tagData;
        return innerAcc;
      }, {});
      return { ...acc, ...brandCompetitorTagsById };
    }, {});
  });

  function filterAccountsWithTagDataType(accountData) {
    if (useTags.value) {
      return accountData.data_type === DATA_TYPES.TAG;
    }
    if (useSentiment.value) {
      return accountData.data_type === DATA_TYPES.SENTIMENT;
    }
    if (useMessageType.value) {
      return accountData.data_type === DATA_TYPES.MESSAGE_TYPE;
    }
    if (useAssignee.value) {
      return accountData.data_type === DATA_TYPES.ASSIGNEE;
    }
    return true;
  }

  function filterAccountsWithoutAccess(accountData) {
    const skipDataTypes = [
      DATA_TYPES.BENCHMARK,
      DATA_TYPES.BENCHMARK,
      DATA_TYPES.PUBLISHER_PLATFORM,
      DATA_TYPES.COUNTRY,
      DATA_TYPES.REGION,
      DATA_TYPES.AGE_GENDER,
      DATA_TYPES.TAG,
      DATA_TYPES.SENTIMENT,
      DATA_TYPES.MESSAGE_TYPE,
      DATA_TYPES.ASSIGNEE,
      DATA_TYPES.CHANNEL,
      ...Object.values(LISTENING_DATA_TYPES),
    ];

    const uniqueSkipDataTypes = [...new Set(skipDataTypes)];
    if (uniqueSkipDataTypes.includes(accountData?.data_type)) return true;
    if (accountData?.data_type === DATA_TYPES.COMPETITOR) {
      return accountData?.user_has_access && canAccessCompetitor(accountData);
    }
    return accountData?.user_has_access;
  }

  function filterCompetitorAverage(accountData) {
    return accountData?.data_type !== DATA_TYPES.COMPETITOR_AVERAGE || showCompetitorAverage.value;
  }

  function getXAndYData(rawData, label) {
    const formattedData = isLineChart.value ? rawData : pick(rawData, 'value');

    return map(formattedData, (value, date) => {
      const xValue = isLineChart.value ? date : label;
      return {
        x: xValue,
        y: value,
      };
    });
  }

  function getAccountLabel(accountData) {
    if (accountData?.data_type === DATA_TYPES.BENCHMARK) {
      return `${accountData.name} - ${accountData?.follower_bucket}`;
    }
    return accountData.name;
  }

  function getAccountAvatar(accountData) {
    if (
      [DATA_TYPES.BRAND, DATA_TYPES.COMPETITOR, DATA_TYPES.CREATOR].includes(accountData?.data_type)
    ) {
      return accountData.avatar;
    }
    return undefined;
  }

  function getDatasetForAccount(accountData) {
    const label = getAccountLabel(accountData);
    const avatar = getAccountAvatar(accountData);

    const useAllChannelsKey =
      isCustomMetricReportTile.value ||
      useTags.value ||
      (!isLineChart.value && aggregateBy.value === AGGREGATE_REPORT_TYPES.BRAND.value) ||
      isCommunityReportCategory.value ||
      isListeningReportCategory.value;
    const channelKey = useAllChannelsKey ? ALL_CHANNELS_KEY : firstChannel.value;
    // Some ads metrics are still on the old metric storage format
    const rawAccountData =
      accountData.metrics?.[firstMetric.value]?.[channelKey] ??
      accountData.metrics?.[firstMetric.value];
    const data = getXAndYData(rawAccountData, label);
    const accountGraphData = {
      label,
      legend: { avatar },
      data,
      spanGaps: true,
    };

    if (aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
      accountGraphData.borderColor = sentimentColorMap[accountData.name.toUpperCase()];
    }
    const dottedDataTypes = [DATA_TYPES.BENCHMARK, DATA_TYPES.COMPETITOR_AVERAGE];
    if (isLineChart.value && dottedDataTypes.includes(accountData.data_type)) {
      accountGraphData.segment = {
        borderDash: [6, 6],
      };
    }
    if (isLineChart.value && accountGraphData.data.length === 1) {
      accountGraphData.pointRadius = 2;
    }
    return accountGraphData;
  }

  function getStackedDatasetForAds() {
    const dataObj = Object.values(formattedReportDataForTableAndGraphReports.value ?? {}).reduce(
      (acc, breakdownData) => {
        const xValue = breakdownData.name;
        const secondaryBreakdown = breakdownData.metrics?.[firstMetric.value] ?? {};
        Object.values(secondaryBreakdown).forEach((secondaryBreakdownData) => {
          const label = secondaryBreakdownData.name;
          if (!acc?.[label]) {
            acc[label] = { label, data: [] };
          }
          acc[label].data.push({
            x: xValue,
            y: secondaryBreakdownData.value,
          });
        });
        return acc;
      },
      {},
    );
    return Object.values(dataObj);
  }

  function addTagNames(data) {
    Object.keys(data).forEach((tagDataStr) => {
      const tagId = Number(tagDataStr.split('_')[0] ?? '0');
      if (tagDataStr.includes(`_${DATA_TYPES.BRAND}`)) {
        data[tagDataStr].name = authStore.brandTagsById?.[tagId]?.name;
      } else if (tagDataStr.includes(`_${DATA_TYPES.COMPETITOR}`)) {
        data[tagDataStr].name = competitorTagsById.value?.[tagId]?.name;
      }
    });
  }

  function getStackedDatasetWithSecondaryAggregate() {
    const datasets = [];

    const allPrimaryBreakdownEntries = Object.entries(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).filter(([key, value]) => {
      if (key === 'metrics') return false;
      return !(aggregateBy.value && value.data_type !== aggregateBy.value);
    });

    const secondaryKeys = new Map();
    allPrimaryBreakdownEntries.forEach(([, primaryBreakdownData]) => {
      if (primaryBreakdownData.breakdowns) {
        Object.keys(primaryBreakdownData.breakdowns).forEach((key) => {
          secondaryKeys.set(key, primaryBreakdownData.breakdowns[key]);
        });
      }
    });

    secondaryKeys.forEach((secondaryBreakdownData, secondaryKey) => {
      const data = allPrimaryBreakdownEntries
        .filter(([, primaryBreakdownData]) => filterAccountsWithoutAccess(primaryBreakdownData))
        .filter(([, primaryBreakdownData]) => filterCompetitorAverage(primaryBreakdownData))
        .map(([, primaryBreakdownData]) => {
          const breakdownData = primaryBreakdownData.breakdowns?.[secondaryKey] ?? {};
          const { metrics: breakdownMetrics, data_type: breakdownDataType } = breakdownData;
          const channelKey =
            breakdownDataType === DATA_TYPES.CHANNEL ? secondaryKey : ALL_CHANNELS_KEY;
          const value = breakdownMetrics?.[firstMetric.value]?.[channelKey]?.value || 0;
          const xLabel = primaryBreakdownData?.name || primaryBreakdownData?.label;
          return { x: xLabel, y: value };
        });

      const hasNonZeroValue = data.some((point) => point.y !== 0);
      if (hasNonZeroValue) {
        const dataset = {
          label: secondaryBreakdownData?.name || secondaryBreakdownData?.label,
          data,
          spanGaps: true,
        };
        if (secondaryAggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
          dataset.borderColor = sentimentColorMap[secondaryKey];
        }
        datasets.push(dataset);
      }
    });

    return datasets;
  }

  function formatHour(hour) {
    return dayjs().hour(hour).minute(0).format('h:mm a');
  }

  function getTimeSeriesStackedDataset() {
    const allPrimaryBreakdownEntries = Object.entries(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).filter(([key]) => key !== 'metrics');

    // Note: function currently hardcoded for ONLINE_FOLLOWERS
    //  Slices the bars into 8 chunks of 3 hour blocks, 12am-3am, 3am-6am etc
    const barBucketIndexFunc = (hour) => floor(hour / ONLINE_FOLLOWER_HOUR_BUCKET_SIZE); // Replace with other function for non-Online followers/hourly data

    // Collect all unique dates across all breakdowns and sort chronologically
    const allDates = [
      ...new Set(
        allPrimaryBreakdownEntries.flatMap(([, breakdown]) => {
          const metricData =
            breakdown.metrics?.[firstMetric.value]?.[firstChannel.value] ??
            breakdown.metrics?.[firstMetric.value]?.[ALL_CHANNELS_KEY] ??
            {};
          return Object.keys(metricData);
        }),
      ),
    ].sort();

    // Pre-populate buckets with all dates in sorted order and hour components at zero
    const buckets = [];
    for (let hourGroup = 0; hourGroup < 24; hourGroup += ONLINE_FOLLOWER_HOUR_BUCKET_SIZE) {
      const bucketLabel = `${formatHour(hourGroup)} - ${formatHour(hourGroup + ONLINE_FOLLOWER_HOUR_BUCKET_SIZE)}`;
      buckets.push({
        label: bucketLabel,
        spanGaps: true,
        data: allDates.map((date) => ({
          x: formatDatePattern(date, 'MMM D'),
          y: 0,
          components: Array.from({ length: ONLINE_FOLLOWER_HOUR_BUCKET_SIZE }, (_, i) => ({
            label: `${formatHour(hourGroup + i)} - ${formatHour(hourGroup + i + 1)}`,
            value: 0,
          })),
        })),
      });
    }

    allPrimaryBreakdownEntries.forEach(([key, breakdown]) => {
      const metricData =
        breakdown.metrics?.[firstMetric.value]?.[firstChannel.value] ??
        breakdown.metrics?.[firstMetric.value]?.[ALL_CHANNELS_KEY] ??
        {};

      Object.entries(metricData).forEach(([date, bucket]) => {
        if (isNil(bucket)) return;
        const formattedDate = formatDatePattern(date, 'MMM D');
        Object.entries(bucket).forEach(([hourKey, bucketValue]) => {
          const bucketIndex = barBucketIndexFunc(hourKey);
          const slice = buckets[bucketIndex].data.find((dateInfo) => dateInfo.x === formattedDate);
          if (slice) {
            slice.y += bucketValue;
            const hourLabel = `${formatHour(Number(hourKey))} - ${formatHour(Number(hourKey) + 1)}`;
            const component = slice.components.find((c) => c.label === hourLabel);
            if (component) {
              component.value += bucketValue;
            }
          }
        });
      });
    });

    return buckets;
  }

  function getStackedDatasetForAccount() {
    if (isAdsStackedBarChart.value) return getStackedDatasetForAds();

    /*
    Check if secondary aggregate is explicitly set OR if any data entries have breakdowns
    This handles owned reports with non-additive metrics that have breakdowns without
    explicit secondary_aggregate_by in meta
     */
    const hasBreakdowns = Object.values(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).some((entry) => entry?.breakdowns && Object.keys(entry.breakdowns).length > 0);

    if (secondaryAggregateBy.value || hasBreakdowns) {
      return getStackedDatasetWithSecondaryAggregate();
    }

    return reportChannels.value?.map((channel) => {
      const label = isTSIReportCategory.value
        ? getChannelConfigProp(channel, 'chartLegend') || getChannelText(channel)
        : getChannelText(channel);
      const data = Object.values(reportData.value ?? {})
        .filter((accountData) => filterAccountsWithoutAccess(accountData))
        .filter((accountData) => filterCompetitorAverage(accountData))
        .map((accountData) => ({
          x: accountData.name,
          y: accountData.metrics?.[firstMetric.value]?.[channel]?.value,
        }));
      return { label, data, spanGaps: true };
    });
  }

  function getDatasetForAgeGender() {
    const ageGenderObj = Object.values(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).reduce((acc, demographicData) => {
      const splitData = demographicData.name.split(' ');
      const label = splitData[0];
      if (!acc?.[label]) {
        acc[label] = {
          label,
          spanGaps: true,
          data: [],
        };
      }
      acc[label].data.push({
        x: splitData[1],
        y: demographicData.metrics?.[firstMetric.value]?.value,
      });
      return acc;
    }, {});
    return Object.values(ageGenderObj);
  }

  function getDatasetForChannel(channel, rootData, labelProperty = 'text') {
    const defaultLabel = getChannelText(channel);
    const label =
      labelProperty === 'text'
        ? defaultLabel
        : getChannelConfigProp(channel, labelProperty) || defaultLabel;

    const rawChannelData =
      rootData?.[channel]?.metrics?.[firstMetric.value]?.[channel] ??
      rootData?.metrics?.[firstMetric.value]?.[channel];
    const data = getXAndYData(rawChannelData, label);
    const dataset = {
      label,
      data,
      ...(isLineChart.value && data.length === 1 && { pointRadius: 2 }),
    };
    return {
      ...dataset,
      spanGaps: true,
    };
  }

  function getDatasetForMetric(accountData) {
    return (metric) => {
      const { mediaTypeLabel } = useMetricDetails({
        metric,
        channels: reportChannels,
        metricReportTypeKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
      });
      const rawMetricData = accountData?.metrics?.[metric]?.[firstChannel.value];
      const data = getXAndYData(rawMetricData, mediaTypeLabel.value);
      return {
        label: mediaTypeLabel.value,
        data,
        spanGaps: true,
        ...(isLineChart.value && data.length === 1 && { pointRadius: 2 }),
      };
    };
  }

  const rawGraphReportData = computed(() => {
    // Don't modify the raw data here, formattedReportDataForTableAndGraphReports should
    // be modified to contain additional fields like name, label, etc.
    const data = formattedReportDataForTableAndGraphReports.value ?? {};
    if (useTags.value) addTagNames(data);
    if (isCreatorReport.value) {
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (Object.values(value?.metrics ?? {}).length === 0) {
          acc[key] = null;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
    }
    return data;
  });

  function getGenericDataset() {
    return Object.entries(rawGraphReportData.value).reduce((acc, [groupKey, groupedData]) => {
      const isTotalReport = aggregateBy.value !== LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value;
      const skipGroup =
        (isTotalReport && groupKey === 'metrics') || (!isTotalReport && groupKey !== 'metrics');
      if (skipGroup) return acc;

      let rawData;
      if (groupKey === 'metrics') {
        rawData = groupedData[firstMetric.value]?.[ALL_CHANNELS_KEY];
      } else {
        const isAggregatedByChannel =
          aggregateBy.value === BASE_BREAKDOWN_OPTIONS.CHANNEL.value ||
          (isNil(aggregateBy.value) && reportChannels.value?.length > 1);
        const channelKey = isAggregatedByChannel ? groupKey : ALL_CHANNELS_KEY;
        rawData = groupedData.metrics?.[firstMetric.value]?.[channelKey];
      }

      const label = groupedData?.name;

      const data = getXAndYData(rawData, label);
      const graphData = {
        label,
        data,
        spanGaps: true,
      };
      if (aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
        graphData.borderColor = sentimentColorMap[groupKey];
      }
      if (isLineChart.value && graphData.data.length === 1) {
        graphData.pointRadius = 2;
      }
      acc.push(graphData);
      return acc;
    }, []);
  }

  const { horizontalBarChartData, horizontalBarChartOptions, showHorizontalLegend } =
    useHorizontalBarReportDetails({
      rawGraphReportData,
      report,
    });

  const showLegend = computed(
    () =>
      (aggregateBy.value !== LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value &&
        !isHorizontalBarChart.value) ||
      (showHorizontalLegend.value && isHorizontalBarChart.value),
  );

  const graphReportData = computed(() => {
    if (isTimeSeriesStackedBarChart.value) {
      return getTimeSeriesStackedDataset();
    }
    if (isStackedBarChart.value) {
      return getStackedDatasetForAccount();
    }
    if (useChannelDataset.value) {
      const rootData =
        isLineChart.value && !isCommunityReportCategory.value && !isListeningReportCategory.value
          ? (Object.values(rawGraphReportData.value)?.[0] ?? {})
          : rawGraphReportData.value;
      return reportChannels.value.map((channel) =>
        getDatasetForChannel(channel, rootData, isTSIReportCategory.value ? 'chartLegend' : 'text'),
      );
    }
    if (useMetricDataset.value) {
      const accountData = Object.values(rawGraphReportData.value)?.[0] ?? {};
      return metrics.value.map(getDatasetForMetric(accountData));
    }
    if (useAgeGenderDataset.value) {
      return getDatasetForAgeGender();
    }
    if (isListeningReportCategory.value) {
      return getGenericDataset();
    }
    const filteredData = Object.values(rawGraphReportData.value)
      .filter((accountData) => filterAccountsWithTagDataType(accountData))
      .filter((accountData) => filterAccountsWithoutAccess(accountData))
      .filter((accountData) => filterCompetitorAverage(accountData));
    return filteredData.map((accountData) => getDatasetForAccount(accountData));
  });

  function sortStackedChartData(chartData, sort = 'DESC', maxBars = null) {
    // Get totals for each bar
    const totals = chartData.reduce((acc, bar) => {
      bar.data.forEach((slice) => {
        if (!acc[slice.x]) {
          acc[slice.x] = slice.y;
        } else {
          acc[slice.x] += slice.y;
        }
      });
      return acc;
    }, {});

    // Sort by the height of each bar
    const resultData = cloneDeep(chartData);
    if (!disableAggregationSort.value) {
      resultData.forEach((bar) => {
        bar.data.sort((a, b) => {
          const aTotal = totals[a.x] ?? 0;
          const bTotal = totals[b.x] ?? 0;
          if (sort.toLowerCase() === 'asc') {
            return aTotal - bTotal;
          }
          if (sort.toLowerCase() === 'desc') {
            return bTotal - aTotal;
          }
          return 0;
        });
      });
    }
    // Optionally slice to desired number of bars
    if (isNumber(maxBars)) {
      resultData.forEach((bar) => {
        bar.data = bar.data.slice(0, maxBars);
      });
    }

    return resultData;
  }

  const prioritizedGraphReportData = computed(() => {
    if (isHorizontalBarChart.value) return horizontalBarChartData.value;
    if (isStackedBarChart.value) {
      if (isTimeSeriesStackedBarChart.value) return graphReportData.value;
      return sortStackedChartData(graphReportData.value, 'DESC', CHART_DATA_LIMIT);
    }
    const sortedData = disableAggregationSort.value
      ? graphReportData.value
      : sortChartData(graphReportData.value);
    return sortedData.slice(0, CHART_DATA_LIMIT);
  });

  function calculateDurationStepSize(min, max) {
    if (min === max) return 1;
    const range = max - min;
    const numSteps = 5;
    const step = Math.ceil(range / numSteps);

    if (range < 60 * numSteps) {
      if (range <= 30) {
        return 5;
      }
      if (range <= 60) {
        return 10;
      }
      if (range <= 150) {
        return 15;
      }
      return 30;
    }
    if (range < 3600 * numSteps) {
      return Math.ceil(step / 60) * 60;
    }
    return Math.ceil(step / 3600) * 3600;
  }

  const chartStepSize = computed(() => {
    if (!isDurationMetric.value || !prioritizedGraphReportData.value.length) {
      return undefined;
    }

    const allValues = [];
    prioritizedGraphReportData.value.forEach((dataset) => {
      dataset.data.forEach((point) => {
        allValues.push(point.y);
      });
    });

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    return calculateDurationStepSize(min, max);
  });

  const lineChartOptions = computed(() => ({
    scales: {
      x: {
        type: 'time',
        time: {
          tooltipFormat: TIME_FORMATS?.[timeUnit.value] || DEFAULT_TIME_FORMAT,
          unit: timeUnit.value || 'day',
        },
      },
      y: {
        ticks: {
          callback: (value) => getFormattedValue(value, false),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const stackedBarChartOptions = computed(() => ({
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => getFormattedValue(value),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const barChartOptions = computed(() => ({
    grouped: useAgeGenderDataset.value,
    scales: {
      y: {
        ticks: {
          callback: (value) => getFormattedValue(value, false),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const chartOptions = computed(() => {
    if (isLineChart.value) return lineChartOptions.value;
    if (isHorizontalBarChart.value) return horizontalBarChartOptions.value;

    if (isStackedBarChart.value) return stackedBarChartOptions.value;
    if (isBarChart.value) return barChartOptions.value;
    return {};
  });

  function timeSeriesGroupedStackedBarTooltipCallback(context) {
    let datasetIndex = 0;
    const components = context.tooltip?.dataPoints
      ?.map((slice) =>
        slice.raw.components.map((component, i) => {
          datasetIndex = slice.datasetIndex;
          return {
            label: component.label,
            value: getFormattedValue(component.value, false),
            divider: i === 0, // divider for title
          };
        }),
      )
      .flat();
    const minHour = datasetIndex * ONLINE_FOLLOWER_HOUR_BUCKET_SIZE;
    const maxHour = minHour + ONLINE_FOLLOWER_HOUR_BUCKET_SIZE;
    const titleLabel = `${context.tooltip.title[0]}, ${formatHour(minHour)} - ${formatHour(maxHour)}`;
    const title = [{ label: titleLabel, value: '' }];

    return title.concat(components);
  }

  const graphCustomTooltipCallback = computed(() => {
    if (isTimeSeriesStackedBarChart.value) {
      return timeSeriesGroupedStackedBarTooltipCallback;
    }
    return undefined;
  });

  function generateCustomTooltip({ tooltip }) {
    if (!tooltip) return {};
    const dataPoint = tooltip?.dataPoints?.[0];
    if (!dataPoint) return {};

    let label = '';
    if (isCustomMetricReportTile.value) {
      label = customMetricDetails.value.name;
    } else {
      label = reportMetricDetails.value.displayName;
    }

    return {
      title: tooltip.title[0],
      dataPoints: [
        {
          label,
          value: getFormattedValue(Number(dataPoint.raw.y), true),
        },
      ],
    };
  }

  return {
    chartOptions,
    chartFormatConfig,
    generateCustomTooltip,
    goalValues,
    graphType,
    isStackedBarChart,
    prioritizedGraphReportData,
    showLegend,
    graphCustomTooltipCallback,
  };
}
